import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getItemBasicDetails, getItemRetail } from "src/api/bi";
import { resetStore } from "src/store/actions";
import { ItemBasicDetailsInitialState, RecentRetail } from "../interface";
import stringToNumber from "src/utils/stringToNumber";
import { ITEM_RETAIL_TYPE_MAPPER } from "src/config/constants/genericConstants";

export const initialState: ItemBasicDetailsInitialState = {
  data: null,
  status: "initial",
  error: "",
  recentRetail: null
};

export interface BasicDetailsArgs {
  itemId: string;
  storeId: string;
}

export const fetchItemBasicDetails = createAsyncThunk(
  "itemDetails/fetchItemBasicDetails",
  ({ itemId, storeId }: BasicDetailsArgs) =>
    getItemBasicDetails({ itemId, storeId })
);

export const fetchItemRetail = createAsyncThunk(
  "itemDetails/fetchItemRetail",
  ({ itemId, storeId }: BasicDetailsArgs) => getItemRetail({ itemId, storeId })
);

const itemBasicDetailsSlice = createSlice({
  name: "itemBasicDetails",
  initialState,
  reducers: {
    resetItemBasicDetails: (): ItemBasicDetailsInitialState => ({
      ...initialState,
      status: "idle"
    })
  },
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchItemBasicDetails.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchItemBasicDetails.fulfilled, (state, { payload }) => {
        state.status = "idle";
        if (payload?.data?.itemId) {
          state.data = payload.data;
        } else {
          state.data = null;
        }
      })
      .addCase(fetchItemBasicDetails.rejected, (state) => {
        state.status = "error";
        state.data = null;
      })
      .addCase(fetchItemRetail.fulfilled, (state, { payload }) => {
        const retailDataList = payload?.data;

        if (!Array.isArray(retailDataList) || !retailDataList?.length) {
          return;
        }

        // Get the recent retail by sorting the items by date
        retailDataList.sort(
          (a: RecentRetail, b: RecentRetail) =>
            new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        const recentRetail = payload.data.pop();

        // Add round off for margin
        if (recentRetail?.margin) {
          recentRetail.margin =
            stringToNumber(recentRetail.margin).toFixed(2) + "%";
        }

        // Change type value of warehouse transfer to TRANSFER
        if (ITEM_RETAIL_TYPE_MAPPER[recentRetail?.type]) {
          recentRetail.type = ITEM_RETAIL_TYPE_MAPPER[recentRetail.type];
        }

        state.recentRetail = recentRetail;
      });
  }
});

export const { resetItemBasicDetails } = itemBasicDetailsSlice.actions;

export const selectData = ({
  itemBasicDetails
}: {
  itemBasicDetails: ItemBasicDetailsInitialState;
}) => itemBasicDetails.data;

export const selectApiStatus = ({
  itemBasicDetails
}: {
  itemBasicDetails: ItemBasicDetailsInitialState;
}) => itemBasicDetails.status;

export const selectError = ({
  itemBasicDetails
}: {
  itemBasicDetails: ItemBasicDetailsInitialState;
}) => itemBasicDetails.error;

export default itemBasicDetailsSlice.reducer;
